// surveyQuestions.js
//
// Content of the clinician "Pre-Exposure Survey" (source: Survey Questions V3.docx).
// The survey page renders straight from this file, so wording/options can be
// edited here without touching the UI.
//
// Structure: SECTIONS → pages → questions. Each page is one screen, kept to a
// few questions so respondents are never shown a wall of items at once.
//
// Question types and how their answers are stored (answers is a flat object
// keyed by question id — these keys are what end up in survey_responses.answers):
//   single     — one option's `value` (string). An option with `other: true`
//                reveals a text box saved under `${id}_other`.
//   multi      — array of option values ("select all that apply"); `other`
//                works the same as for single.
//   scale      — ordinal Likert-style scale; saved as its 1-based position
//                (1 = first/lowest label).
//   statements — several agree/disagree statements sharing one scale; each
//                statement is its own answer key, saved like `scale`.
//   text       — free text (string).
// `followUp: true` questions are unnumbered sub-questions; `showIf(answers)`
// hides a question (and drops its answer on save) until it applies.
// `comment: true` marks the optional free-text box that closes each section;
// like follow-ups it is unnumbered and left out of the answered counts.
// A section's `context` is shown in full on its first page and as a collapsible
// reminder on the rest; a page with no questions is a reading-only page.

export const SURVEY_ID = 'pre_exposure_v3';
export const SURVEY_TITLE = 'Pre-Exposure Survey';

export const INTRO_TEXT =
  'Thank you for participating in this survey. This research study is being conducted by Massachusetts General Hospital (MGH) in collaboration with the Massachusetts Institute of Technology (MIT) to inform the design of a decision support model for kidney allocation, with a particular focus on expedited placements (out-of-sequence allocation) decisions. The survey should take approximately 10-15 minutes. Your responses will help us understand clinician perspectives on the appropriate role of model-generated recommendations in this process. Participation is voluntary; you may skip any questions or stop at any time.';

const SCALES = {
  agreement: ['Strongly disagree', 'Disagree', 'Neither agree nor disagree', 'Agree', 'Strongly agree'],
  comfort: ['Not at all comfortable', 'Slightly comfortable', 'Moderately comfortable', 'Very comfortable', 'Extremely comfortable'],
  confidence: ['Not at all confident', 'Slightly confident', 'Moderately confident', 'Very confident', 'Extremely confident'],
  importance: ['Not at all important', 'Slightly important', 'Moderately important', 'Very important', 'Extremely important'],
  usefulness: ['Not at all useful', 'Slightly useful', 'Moderately useful', 'Very useful', 'Extremely useful'],
  likelihood: ['Extremely unlikely', 'Somewhat unlikely', 'Neither likely nor unlikely', 'Somewhat likely', 'Extremely likely'],
  frequency: ['Never', 'Rarely', 'Sometimes', 'Often', 'Routinely'],
  familiarity: ['Not at all', 'Heard of it', 'Somewhat familiar', 'Very familiar'],
};

const opt = (value, label, extra = {}) => ({ value, label, ...extra });
const OTHER = opt('other', 'Other (please specify)', { other: true });

export const ALLOCIQ_DESCRIPTION =
  'AllocIQ is a new tool that, for the first time, gives clinicians direct access to an underlying AI allocation model through a chat-style (LLM-powered) interface. Clinicians can query the model to help evaluate whether a given kidney should be considered for out-of-sequence placement. The underlying prediction model is a decision tree (CART) model trained on UNOS data from 2016-2025 (excluding the COVID-disrupted years 2020-2022), which estimates the likelihood that a kidney will be accepted within 100 offers under standard sequential allocation. Kidneys predicted unlikely to be accepted within 100 offers may be flagged as candidates for out-of-sequence placement. Clinicians can also interact directly with the chat interface to understand why recommendations were made.';

// Optional free-text box at the end of every section, so respondents can
// clarify any of their answers in it. Saved as `<section id>_comments`.
const sectionComments = (sectionId) => ({
  id: `${sectionId}_comments`,
  type: 'text',
  comment: true,
  text: 'Is there anything you would like to clarify or add about your answers in this section?',
  placeholder: 'Optional — e.g., context behind an answer, or a question that was unclear.',
  maxLength: 2000,
});

export const SECTIONS = [
  {
    id: 'background',
    title: 'Respondent Background',
    shortTitle: 'Your background',
    blurb: 'A few questions about your role and experience.',
    pages: [
      {
        questions: [
          {
            id: 'primary_role',
            type: 'single',
            text: 'What is your primary role?',
            options: [
              opt('transplant_surgeon', 'Transplant surgeon'),
              opt('nephrologist', 'Nephrologist'),
              opt('donation_coordinator', 'Donation coordinator'),
              OTHER,
            ],
          },
          {
            id: 'years_in_transplant',
            type: 'single',
            text: 'How many years have you practiced in solid organ transplantation?',
            options: [
              opt('lt5', 'Less than 5 years'),
              opt('5_10', '5-10 years'),
              opt('11_20', '11-20 years'),
              opt('gt20', 'More than 20 years'),
            ],
          },
        ],
      },
      {
        questions: [
          {
            id: 'stats_comfort',
            type: 'scale',
            text: 'How comfortable are you interpreting probabilities or statistical risk estimates (e.g., in clinical guidelines, research findings, or risk calculators)?',
            scale: SCALES.comfort,
          },
          {
            id: 'stats_training',
            type: 'single',
            text: 'Have you had formal training in statistics or biostatistics?',
            options: [opt('yes', 'Yes'), opt('no', 'No')],
          },
        ],
      },
      {
        questions: [
          {
            id: 'cds_frequency',
            type: 'scale',
            text: 'How often do you currently use clinical decision-support tools in your practice (e.g., KDPI, EHR-based risk calculators, transplant scoring, or algorithmic tools)?',
            scale: SCALES.frequency,
          },
          {
            id: 'cds_tools',
            type: 'text',
            followUp: true,
            text: 'Which tools do you use?',
            placeholder: 'e.g., KDPI calculator, EPTS, …',
            // Anything other than "Never" (position 1).
            showIf: (a) => Number(a.cds_frequency) > 1,
          },
          {
            id: 'allociq_familiarity',
            type: 'scale',
            text: 'Before this survey, how familiar were you with AllocIQ?',
            scale: SCALES.familiarity,
          },
          sectionComments('background'),
        ],
      },
    ],
  },
  {
    id: 'out_of_sequence',
    title: 'Experience with Out-of-Sequence Placement',
    shortTitle: 'Out-of-sequence experience',
    blurb: 'Your recent experience with expedited (out-of-sequence) kidney placement.',
    context: {
      title: 'What is out-of-sequence allocation?',
      reminder: 'Re-read the definition of out-of-sequence allocation',
      text: 'Out-of-sequence allocation is when an organ is offered, accepted, or transplanted into a transplant candidate or potential transplant recipient (PTR) in a way that deviates from the match sequence and is not consistent with OPTN policy (Health Resources and Services Administration, 2025). This policy is intended to reduce the number of organs that go unused due to declines earlier in the match sequence.',
    },
    pages: [
      {
        questions: [
          {
            id: 'oos_kidneys_evaluated',
            type: 'single',
            text: 'In the past 12 months, approximately how many kidneys have you personally evaluated specifically under expedited placement (out-of-sequence allocation)?',
            options: [
              opt('0', '0'),
              opt('1_2', '1-2'),
              opt('3_5', '3-5'),
              opt('6_10', '6-10'),
              opt('gt10', 'More than 10'),
              opt('not_sure', 'Not sure'),
            ],
            columns: 3,
          },
          {
            id: 'oos_role',
            type: 'multi',
            followUp: true,
            text: 'In these out-of-sequence decisions, what is typically your role?',
            options: [
              opt('initiated', 'Initiated or proposed the out-of-sequence request'),
              opt('primary_decision_maker', 'Primary decision-maker on accept/decline'),
              opt('consulting', 'Provided a secondary or consulting opinion'),
              opt('relayed', 'Relayed or carried out a decision made by others'),
              OTHER,
            ],
            showIf: (a) => !!a.oos_kidneys_evaluated && a.oos_kidneys_evaluated !== '0',
          },
        ],
      },
      {
        questions: [
          {
            id: 'oos_difficulty',
            type: 'multi',
            text: 'Think of a specific out-of-sequence case in the past year. What made that decision difficult?',
            options: [
              opt('insufficient_info', 'Insufficient information about the candidate or organ'),
              opt('not_enough_time', 'Not enough time to gather information'),
              opt('outcome_uncertainty', 'Uncertainty about the likely outcome if accepted'),
              opt('coordination', 'Difficulty reaching or coordinating with other centers'),
              opt('team_disagreement', 'Disagreement among the care team'),
              OTHER,
            ],
          },
          {
            id: 'oos_info_confidence',
            type: 'scale',
            text: 'How confident are you that you typically have enough information to make a well-informed out-of-sequence decision?',
            scale: SCALES.confidence,
          },
        ],
      },
      {
        questions: [
          {
            id: 'oos_helpful_info',
            type: 'multi',
            text: 'What information, if available earlier, would most help you make expedited placement decisions?',
            options: [
              opt('predicted_nonuse_risk', 'Predicted risk that the organ goes unused'),
              opt('center_capacity', 'List of centers with current capacity to accept'),
              opt('comparable_outcomes', 'Outcomes from comparable past cases'),
              opt('cold_ischemia_countdown', 'Real-time cold ischemia countdown'),
              OTHER,
            ],
          },
          sectionComments('out_of_sequence'),
        ],
      },
    ],
  },
  {
    id: 'ai_beliefs',
    title: 'Baseline Beliefs and Trust about AI and Organ Allocation',
    shortTitle: 'Beliefs about AI',
    blurb: 'Your general views on AI tools in organ allocation.',
    pages: [
      {
        questions: [
          {
            id: 'ai_bias_trust',
            type: 'statements',
            text: 'To what extent do you agree with the following statements?',
            scale: SCALES.agreement,
            items: [
              { id: 'ai_mitigate_bias', text: 'An AI-based allocation tool could mitigate bias in organ allocation decisions.' },
              { id: 'ai_worsen_bias', text: 'I am concerned that an AI-based allocation tool could introduce or worsen bias against certain patient populations.' },
              { id: 'ai_trust_high_stakes', text: 'In general, I trust AI-based tools to perform reliably in high-stakes medical decisions.' },
            ],
          },
        ],
      },
      {
        questions: [
          {
            id: 'ai_evidence',
            type: 'multi',
            text: 'What level or type of evidence do you look for in deciding whether and when to use a tool with AI?',
            options: [
              opt('peer_reviewed', 'Availability of peer-reviewed literature'),
              opt('us_clinical_trials', 'U.S. clinical trials outlining the efficacy of the outcomes'),
              opt('physician_involvement', 'Acknowledgment and transparency of physician involvement throughout the product development process'),
              opt('regulatory_status', 'Regulatory clearance or approval status'),
              OTHER,
            ],
          },
          {
            id: 'ai_explain_importance',
            type: 'scale',
            text: 'How important is it to you that an AI tool explain the reasoning behind its recommendation?',
            scale: SCALES.importance,
          },
        ],
      },
      {
        questions: [
          {
            id: 'ai_tool_role',
            type: 'single',
            text: 'An AI allocation tool should only be…',
            options: [
              opt('advisory_only', 'Advisory only'),
              opt('decision_support_override', 'Decision support with override'),
              opt('automated_matching', 'I would accept automated matching'),
            ],
          },
          {
            id: 'ai_liability_block',
            type: 'statements',
            text: 'How much do you agree with the following statement?',
            scale: SCALES.agreement,
            items: [
              { id: 'ai_liability_concern', text: 'I am concerned about liability or legal risk associated with following an AI-generated recommendation in an out-of-sequence decision.' },
            ],
          },
          sectionComments('ai_beliefs'),
        ],
      },
    ],
  },
  {
    id: 'allociq',
    title: 'Expectations of AllocIQ',
    shortTitle: 'About AllocIQ',
    blurb: 'Your first impressions of AllocIQ, based on a short description.',
    context: {
      title: 'About AllocIQ',
      reminder: 'Re-read the description of AllocIQ',
      text: ALLOCIQ_DESCRIPTION,
    },
    pages: [
      // Reading-only page so respondents see the description before rating it.
      { questions: [] },
      {
        questions: [
          {
            id: 'allociq_trust',
            type: 'statements',
            text: 'Please rate your agreement with each statement about AllocIQ.',
            scale: SCALES.agreement,
            items: [
              { id: 'allociq_accurate', text: 'I believe AllocIQ’s recommendations would be accurate.' },
              { id: 'allociq_patient_interest', text: 'I believe AllocIQ was developed with patients’ best interests in mind.' },
              { id: 'allociq_comfortable_following', text: 'I would feel comfortable following AllocIQ’s recommendation in an out-of-sequence decision.' },
              { id: 'allociq_understand_why', text: 'I understand why AllocIQ would make a given recommendation.' },
            ],
          },
        ],
      },
      {
        questions: [
          {
            id: 'allociq_expected_usefulness',
            type: 'scale',
            text: 'How useful do you expect a tool like this to be in supporting your decision-making in expedited placement decisions?',
            scale: SCALES.usefulness,
          },
          {
            id: 'allociq_consult_likelihood',
            type: 'scale',
            text: 'If AllocIQ were available today, how likely would you be to consult its recommendations before making an out-of-sequence decision?',
            scale: SCALES.likelihood,
          },
          {
            id: 'ai_advantage_block',
            type: 'statements',
            text: 'How much do you agree with the following statement?',
            scale: SCALES.agreement,
            items: [
              { id: 'ai_gives_advantage', text: 'Overall, AI-based tools give me an advantage in caring for my patients.' },
            ],
          },
          sectionComments('allociq'),
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Follow-ups and section comment boxes don't get a number.
const isNumbered = (q) => !q.followUp && !q.comment;

// Number questions in reading order (statements count once per statement) so
// the UI matches the numbering in the source doc.
const NUMBERS = (() => {
  const map = {};
  let n = 0;
  SECTIONS.forEach((s) => s.pages.forEach((p) => p.questions.forEach((q) => {
    if (!isNumbered(q)) return;
    if (q.type === 'statements') q.items.forEach((it) => { map[it.id] = ++n; });
    else map[q.id] = ++n;
  })));
  return map;
})();

export const questionNumber = (id) => NUMBERS[id];

export const isVisible = (q, answers) => !q.showIf || q.showIf(answers);

// Answer keys a question owns (statements own one key per statement).
const answerKeys = (q) => (q.type === 'statements' ? q.items.map((it) => it.id) : [q.id]);

const hasValue = (v) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && String(v).trim() !== '');

// Answer keys of the numbered questions on a list of pages. Follow-ups and
// comment boxes are left out so totals stay fixed and optional extras are never
// flagged as unanswered.
const numberedKeys = (pages, answers = {}) =>
  pages.flatMap((p) => p.questions.filter((q) => isNumbered(q) && isVisible(q, answers)).flatMap(answerKeys));

// [answered, total] across the numbered questions of a list of pages.
export const countAnswered = (pages, answers) => {
  const keys = numberedKeys(pages, answers);
  return [keys.filter((k) => hasValue(answers[k])).length, keys.length];
};

// Question numbers on a list of pages that have no answer yet.
export const unansweredNumbers = (pages, answers) =>
  numberedKeys(pages, answers).filter((k) => !hasValue(answers[k])).map((k) => NUMBERS[k]);

// Numbers of the questions on a list of pages, e.g. [1, 2, 3, 4, 5, 6].
export const questionNumbers = (pages) => numberedKeys(pages).map((k) => NUMBERS[k]);

// "Q1–6" / "Q7" label for a list of question numbers ('' when there are none).
export const rangeLabel = (nums) => {
  if (!nums.length) return '';
  const lo = Math.min(...nums);
  const hi = Math.max(...nums);
  return lo === hi ? `Q${lo}` : `Q${lo}–${hi}`;
};

// Drop answers to questions that are currently hidden (e.g. a follow-up whose
// trigger was un-selected) and "other" text whose option is no longer chosen.
export const pruneAnswers = (answers) => {
  const out = { ...answers };
  SECTIONS.forEach((s) => s.pages.forEach((p) => p.questions.forEach((q) => {
    const keys = answerKeys(q);
    if (!isVisible(q, answers)) {
      keys.forEach((k) => delete out[k]);
      delete out[`${q.id}_other`];
      return;
    }
    if (q.type === 'single' || q.type === 'multi') {
      const v = out[q.id];
      const otherChosen = Array.isArray(v) ? v.includes('other') : v === 'other';
      if (!otherChosen) delete out[`${q.id}_other`];
    }
  })));
  return out;
};
